import React, { useState, useEffect } from "react";
import { Form, Row, Col, Button, Alert, Modal, Table } from "react-bootstrap";
import { PencilSquare, Collection } from "react-bootstrap-icons";
import { treatmentTypes, treatmentStatuses, toothConditions } from "../dentist/data/teethData";
import { showToast } from "../../reusables/CommonFunction";
import { formatTo2Decimals } from '../dentist/Formatter';

const EditBulkTreatmentForm = ({ show, bulkTreatment, onSave, onCancel }) => {
    const plan = bulkTreatment?.plan || {};
    const bulkData = bulkTreatment?.bulkPaymentData || {};
    const selectedTeethCount = bulkTreatment?.teethCount || 1;

    const [formData, setFormData] = useState({
        condition: "healthy",
        treatment_type: "",
        treatment_date: "",
        cost: "",
        treatment_status: "planned",
        note: "",
        discount_percentage: "",
        discount_amount: "",
        payment_type: "full",
        initial_payment: "",
        payment_method: "cash",
        isNew: false
    });

    const [calculatedAmounts, setCalculatedAmounts] = useState({
        originalCost: 0,      // per tooth
        discountAmount: 0,    // per tooth
        finalCost: 0,         // per tooth
        paidAmount: 0,        // per tooth
        remainingBalance: 0,  // per tooth
        totalCost: 0,
        totalPaid: 0,
        totalRemaining: 0
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let updated = { ...formData, [name]: value };

        if (name === "discount_percentage" && value) {
            updated.discount_amount = "";
        } else if (name === "discount_amount" && value) {
            updated.discount_percentage = "";
        }
        setFormData(updated);

        if (["discount_percentage", "discount_amount"].includes(name)) {
            calculateAmounts(updated);
        }
    };

    const calculateAmounts = (data) => {
        const perToothCost = formatTo2Decimals(data.cost) || 0;
        const totalOriginal = formatTo2Decimals(perToothCost * selectedTeethCount);

        let discountTotal = 0;
        if (data.discount_percentage) {
            discountTotal = formatTo2Decimals((totalOriginal * parseFloat(data.discount_percentage)) / 100);
        } else if (data.discount_amount) {
            discountTotal = formatTo2Decimals(data.discount_amount);
        }

        const discountPerTooth = formatTo2Decimals(discountTotal / selectedTeethCount);
        const finalCostPerTooth = formatTo2Decimals(perToothCost - discountPerTooth);
        const totalFinalCost = formatTo2Decimals(finalCostPerTooth * selectedTeethCount);

        const totalPaid = formatTo2Decimals(parseFloat(bulkData.paid_amount) || 0);
        const perToothPaid = formatTo2Decimals(totalPaid / selectedTeethCount);

        const totalRemaining = formatTo2Decimals(totalFinalCost - totalPaid);
        const perToothRemaining = formatTo2Decimals(finalCostPerTooth - perToothPaid);

        setCalculatedAmounts({
            originalCost: perToothCost,
            discountAmount: discountPerTooth,
            finalCost: finalCostPerTooth,
            paidAmount: perToothPaid,
            remainingBalance: Math.max(0, perToothRemaining),
            totalCost: totalFinalCost,
            totalPaid: totalPaid,
            totalRemaining: Math.max(0, totalRemaining)
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.treatment_type) {
            showToast("error", "Please select a treatment type.");
            return;
        }
        if (!formData.treatment_date) {
            showToast("error", "Please select a treatment date.");
            return;
        }
        if (!formData.cost || parseFloat(formData.cost) <= 0) {
            showToast("error", "Please enter a valid cost greater than 0.");
            return;
        }

        if (formData.isNew && formData.payment_type === "installment") {
            const maxAllowed = calculatedAmounts.totalCost;
            const entered = parseFloat(formData.initial_payment) || 0;
            if (entered > maxAllowed) {
                showToast("error", `Initial payment cannot be more than ₹${maxAllowed.toFixed(2)}.`);
                return;
            }
        }

        const existingPayments = bulkData?.dental_payment_history || [];
        const updatedBulk = {
            ...bulkData,
            final_cost: calculatedAmounts.totalCost,
            original_cost: calculatedAmounts.originalCost * selectedTeethCount,
            paid_amount: calculatedAmounts.totalPaid,
            remaining_balance: calculatedAmounts.totalRemaining,
            dental_payment_history: existingPayments
        }

        const updatedPlans = Array.from({ length: selectedTeethCount }).map((_, i) => ({
            ...plan,
            treatment_status: formData.treatment_status,
            note: formData.note,
            discount_percentage: formData.isNew ? formData.discount_percentage : plan.discount_percentage,
            discount_amount: formData.isNew ? formData.discount_amount : plan.discount_amount,
            final_cost: calculatedAmounts.finalCost,
            paid_amount: calculatedAmounts.paidAmount,
            remaining_balance: calculatedAmounts.remainingBalance,
            isNew: formData.isNew || false
        }));

        onSave({ plans: updatedPlans, bulkPayments: updatedBulk });
    };

    useEffect(() => {
        if (plan && bulkData) {
            const initData = {
                condition: plan.condition || "healthy",
                treatment_type: plan.treatment_type || "",
                treatment_date: plan.treatment_date || "",
                cost: plan.original_cost || "",
                treatment_status: plan.treatment_status || "planned",
                note: plan.note || "",
                discount_amount: plan.discount_amount !== null ? plan.discount_amount : "",
                discount_percentage: plan.discount_percentage !== null ? plan.discount_percentage : "",
                payment_type: plan.payment_type || "full",
                initial_payment: bulkData.paid_amount || "",
                payment_method: bulkData?.dental_payment_history?.[0]?.amount_paid_via || "cash",
                isNew: plan.isNew || false
            };
            setFormData(initData);
            calculateAmounts(initData);
        }
    }, [plan, bulkData]);

    return (
        <Modal show={show} onHide={onCancel} size="lg" backdrop="static" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <Collection className="me-2" />
                    Bulk Treatment Plan - {selectedTeethCount} Teeth Selected
                </Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {/* Condition */}
                    <Form.Group controlId="condition" className="mb-3">
                        <Form.Label>Tooth Condition</Form.Label>
                        <Form.Select name="condition" value={formData.condition} onChange={handleInputChange} disabled>
                            {toothConditions.map(cond => (
                                <option key={cond.value} value={cond.value}>{cond.label}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    {/* Treatment type & date */}
                    <Row>
                        <Col md={6} className="mb-3">
                            <Form.Group controlId="treatmentType">
                                <Form.Label className="required">Treatment Type</Form.Label>
                                <Form.Select name="treatment_type" value={formData.treatment_type} onChange={handleInputChange} disabled>
                                    <option value="">Select Treatment</option>
                                    {treatmentTypes.map((type) => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Form.Group controlId="treatmentDate">
                                <Form.Label className="required">Treatment Date</Form.Label>
                                <Form.Control type="date" name="treatment_date" value={formData.treatment_date} onChange={handleInputChange} disabled />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Cost & status */}
                    <Row>
                        <Col md={6} className="mb-3">
                            <Form.Group controlId="cost">
                                <Form.Label className="required">Cost per Tooth</Form.Label>
                                <Form.Control type="number" name="cost" value={formData.cost} onChange={handleInputChange} placeholder="0.00" disabled onWheel={(e) => e.target.blur()} />
                            </Form.Group>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Form.Group controlId="status">
                                <Form.Label>Treatment Status</Form.Label>
                                <Form.Select name="treatment_status" value={formData.treatment_status} onChange={handleInputChange}>
                                    {treatmentStatuses.map((status) => (
                                        <option key={status.value} value={status.value}>{status.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Discount */}
                    <Row>
                        <Col md={6} className="mb-3">
                            <Form.Group controlId="discountPercentage">
                                <Form.Label>Discount (%)</Form.Label>
                                <Form.Control type="number" name="discount_percentage" value={formData.discount_percentage} onChange={handleInputChange} placeholder="0" min="0" max="100" disabled={!formData.isNew} onWheel={(e) => e.target.blur()} />
                            </Form.Group>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Form.Group controlId="discountAmount">
                                <Form.Label>Discount Amount (₹)</Form.Label>
                                <Form.Control type="number" name="discount_amount" value={formData.discount_amount} onChange={handleInputChange} placeholder="0.00" disabled={!formData.isNew} onWheel={(e) => e.target.blur()} />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Payment type */}
                    <Form.Group controlId="paymentType" className="mb-3">
                        <Form.Label>Payment Type</Form.Label>
                        <Form.Select name="payment_type" value={formData.payment_type} onChange={handleInputChange} disabled>
                            <option value="full">Pay in Full</option>
                            <option value="installment">Pay in Installments</option>
                        </Form.Select>
                    </Form.Group>

                    {formData.payment_type === "installment" && (
                        <Row>
                            <Col md={6} className="mb-3">
                                <Form.Group controlId="initialPayment">
                                    {formData.isNew ? (
                                        <Form.Label>Initial Payment (₹)</Form.Label>
                                    ) : (
                                        <Form.Label>Already Paid (₹)</Form.Label>
                                    )}
                                    <Form.Control type="number" name="initial_payment" value={formData.initial_payment} onChange={handleInputChange} placeholder="0.00" max={calculatedAmounts.totalCost} disabled onWheel={(e) => e.target.blur()} />
                                </Form.Group>
                            </Col>
                            <Col md={6} className="mb-3 d-flex align-items-end">
                                <small className="text-muted">Remaining: <strong>₹{calculatedAmounts.totalRemaining.toFixed(2)}</strong></small>
                            </Col>
                        </Row>
                    )}

                    {/* Bulk Payment Summary */}
                    {formData.cost && (
                        <Alert variant="info" className="mb-3">
                            <h6 className="mb-3">Bulk Payment Summary ({selectedTeethCount} Teeth)</h6>
                            <Table bordered size="sm" responsive className="mb-0">
                                <tbody>
                                    <tr>
                                        <td>Original Cost</td>
                                        <td className="text-end">₹{(calculatedAmounts.originalCost * selectedTeethCount).toFixed(2)}</td>
                                    </tr>
                                    {formData.discount_percentage && (
                                        <tr>
                                            <td>Discount ({formData.discount_percentage}%)</td>
                                            <td className="text-end text-danger">
                                                -₹{(calculatedAmounts.discountAmount * selectedTeethCount).toFixed(2)}
                                            </td>
                                        </tr>
                                    )}
                                    {formData.discount_amount && !formData.discount_percentage && (
                                        <tr>
                                            <td>Discount (Flat)</td>
                                            <td className="text-end text-danger">
                                                -₹{(calculatedAmounts.discountAmount * selectedTeethCount).toFixed(2)}
                                            </td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td><strong>Final Cost</strong></td>
                                        <td className="text-end">
                                            <strong>₹{calculatedAmounts.totalCost.toFixed(2)}</strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Paid</td>
                                        <td className="text-end text-success">₹{calculatedAmounts.totalPaid.toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td>Remaining</td>
                                        <td className="text-end text-warning">₹{calculatedAmounts.totalRemaining.toFixed(2)}</td>
                                    </tr>


                                    {/* Per-tooth breakdown */}
                                    <tr className="table-light">
                                        <td colSpan={2} className="fw-bold text-center">Per Tooth Breakdown</td>
                                    </tr>
                                    <tr>
                                        <td>Per Tooth Final Cost</td>
                                        <td className="text-end">
                                            ₹{calculatedAmounts.finalCost.toFixed(2)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Per Tooth Paid</td>
                                        <td className="text-end text-success">
                                            ₹{calculatedAmounts.paidAmount.toFixed(2)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Per Tooth Remaining</td>
                                        <td className="text-end text-warning">
                                            ₹{calculatedAmounts.remainingBalance.toFixed(2)}
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Alert>
                    )}
                    {/* Payment method */}
                    <Form.Group controlId="paymentMethod" className="mb-3">
                        <Form.Label>Payment Method</Form.Label>
                        <Form.Select name="payment_method" value={formData.payment_method} onChange={handleInputChange} disabled>
                            <option value="cash">Cash</option>
                            <option value="card">Card</option>
                            <option value="upi">UPI</option>
                            <option value="other">Other</option>
                        </Form.Select>
                    </Form.Group>

                    {/* Notes */}
                    <Form.Group controlId="notes" className="mb-3">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control as="textarea" name="note" value={formData.note} onChange={handleInputChange} rows={3} />
                    </Form.Group>

                    <Alert variant="warning">This plan will apply to all {selectedTeethCount} selected teeth.</Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onCancel}>Cancel</Button>
                    <Button variant="success" type="submit">
                        <PencilSquare className="me-1" /> Update {selectedTeethCount} Plans
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default EditBulkTreatmentForm;
